import { useState } from "react";
import { COULEURS } from "@hoa/protocol";
import { appel, DECISIONS, heure, jourLong, MOMENTS, MOTIFS, pluriel, dateCourte, type BilanComplet } from "./api.js";
import { Kpi, Point, Retour, useLecture } from "./commun.js";
import { useRegie } from "./Regie.js";
import { Pdf, telecharger } from "./pdf.js";
import { ESPACES_HUMAINS } from "../espaces.js";

/**
 * Le bilan d'un événement passé (D5, D8, D10) : tout ce qui a été gardé,
 * rangé en cinq onglets, et le compte rendu en PDF.
 */

const couleurDe = (id: string): string => COULEURS.find((c) => c.id === id)?.color ?? "#666666";

const ONGLETS = [
  ["resume", "Résumé"],
  ["chats", "Chats"],
  ["questions", "Questions"],
  ["tables", "Tables"],
  ["signalements", "Signalements"],
] as const;

export function PageBilan(props: { id: string; onglet: string }): JSX.Element {
  const { aller } = useRegie();
  const l = useLecture(() => appel<BilanComplet>(`/api/regie/evenements/${props.id}/bilan`), null, [props.id]);
  const d = l.donnees;
  const b = d?.bilan;
  const onglet = ONGLETS.some(([k]) => k === props.onglet) ? props.onglet : "resume";

  return (
    <>
      <div className="hd">
        <button type="button" className="rond" aria-label="Retour aux événements" onClick={() => aller({ espace: "regie", page: "evenements" })}>
          <Retour />
        </button>
        <div>
          <h1>{b?.nom ?? "Bilan"}</h1>
          {b ? (
            <div className="sub">
              {jourLong(b.debut)}
              {b.lieu ? ` · ${b.lieu}` : ""}
              {b.gardeJusqua ? ` · données gardées jusqu'au ${dateCourte(b.gardeJusqua)}` : ""}
            </div>
          ) : null}
        </div>
        <span className="sp" />
        <button type="button" className="btn p" disabled={!d} onClick={() => d && compteRendu(d)}>
          Compte rendu PDF
        </button>
      </div>
      <nav className="chips" aria-label="Bilan">
        {ONGLETS.map(([k, lib]) => (
          <button type="button" key={k} className={onglet === k ? "chip on" : "chip"} aria-current={onglet === k ? "page" : undefined} onClick={() => aller({ espace: "regie", page: "bilan", id: props.id, onglet: k })}>
            {lib}
          </button>
        ))}
      </nav>
      {l.erreur ? <p className="erreur">{l.erreur}</p> : null}
      {!d ? <p className="sub">Chargement…</p> : null}
      {d && onglet === "resume" ? <Resume d={d} /> : null}
      {d && onglet === "chats" ? <Chats d={d} /> : null}
      {d && onglet === "questions" ? <Questions d={d} /> : null}
      {d && onglet === "tables" ? <TablesBilan d={d} /> : null}
      {d && onglet === "signalements" ? <SignalementsBilan d={d} /> : null}
    </>
  );
}

function Resume(props: { d: BilanComplet }): JSX.Element {
  const { aller, route } = useRegie();
  const b = props.d.bilan;
  const max = Math.max(1, ...b.frequentation.map((f) => f.n));
  const top = b.parTable.slice().sort((a, c) => c.inscrits - a.inscrits);
  return (
    <>
      <div className="kpis">
        <Kpi valeur={b.participants} libelle={pluriel(b.participants, "participant", "participants")} />
        <Kpi valeur={b.tables} libelle={`${pluriel(b.tables, "table", "tables")} · ${b.tablesReunies} ${pluriel(b.tablesReunies, "réunie", "réunies")}`} />
        <Kpi valeur={b.messages.toLocaleString("fr-FR")} libelle={pluriel(b.messages, "message", "messages")} />
        <Kpi valeur={b.rencontresMoyennes.toLocaleString("fr-FR")} libelle="personnes rencontrées en moyenne" />
        <Kpi valeur={props.d.signalements.length} libelle={pluriel(props.d.signalements.length, "signalement", "signalements")} />
      </div>
      <div className="grille2">
        {ESPACES_HUMAINS && b.frequentation.length === 0 ? null : (
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 className="h2">Présents dans la zone</h2>
          {b.frequentation.length === 0 ? (
            <span className="sub">Aucun relevé.</span>
          ) : (
            <div className="barres">
              {b.frequentation.map((f) => (
                <div key={f.at}>
                  <span className="sub">{f.n}</span>
                  <i style={{ height: `${(f.n / max) * 130}px` }} />
                  <span className="sub">{heure(f.at)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
        )}
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 className="h2">Tables</h2>
          <table>
            <thead>
              <tr>
                <th>Table</th>
                <th>Inscrits</th>
                <th>Arrivés</th>
                <th>Messages</th>
              </tr>
            </thead>
            <tbody>
              {top.slice(0, 5).map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="nom">
                      <Point couleur={couleurDe(t.couleurId)} />
                      <b>{t.nom}</b>
                    </span>
                  </td>
                  <td>{t.inscrits}</td>
                  <td>{t.arrives}</td>
                  <td>{t.messages}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {b.parTable.length > 5 ? (
            <button type="button" className="lien" style={{ alignSelf: "flex-start" }} onClick={() => aller({ espace: "regie", page: "bilan", id: route.id, onglet: "tables" })}>
              Voir les {b.parTable.length} tables
            </button>
          ) : null}
        </section>
      </div>
    </>
  );
}

function Chats(props: { d: BilanComplet }): JSX.Element {
  const b = props.d.bilan;
  const [table, setTable] = useState(b.parTable[0]?.id ?? "");
  const [cherche, setCherche] = useState("");
  const t = b.parTable.find((x) => x.id === table);
  const messages = props.d.messages.filter((m) => m.tableId === table);
  const q = cherche.trim().toLowerCase();
  const visibles = q ? messages.filter((m) => m.text.toLowerCase().includes(q) || m.pseudo.toLowerCase().includes(q)) : messages;
  const personnes = new Set(messages.map((m) => m.pseudo)).size;
  if (b.parTable.length === 0) return <div className="vide">Aucune table.</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
      <nav className="navTables" aria-label="Tables">
        {b.parTable.map((x) => (
          <button type="button" key={x.id} className={x.id === table ? "on" : ""} onClick={() => setTable(x.id)}>
            <Point couleur={couleurDe(x.couleurId)} />
            <span>{x.nom}</span>
            <span>{x.messages}</span>
          </button>
        ))}
      </nav>
      <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 className="h2">
            <Point couleur={couleurDe(t?.couleurId ?? "")} />
            {t?.nom}
            {t?.sujet && t.sujet !== t.nom ? ` · ${t.sujet}` : ""}
          </h2>
          <span className="sp" />
          <span className="sub">
            {messages.length} {pluriel(messages.length, "message", "messages")} · {personnes} {pluriel(personnes, "personne", "personnes")}
          </span>
          <input className="in" style={{ width: 220, height: 40 }} aria-label="Chercher dans le chat" placeholder="Chercher…" value={cherche} onChange={(e) => setCherche(e.target.value)} />
        </div>
        {visibles.length === 0 ? (
          <span className="sub">{messages.length === 0 ? "Aucun message à cette table." : "Rien ne correspond."}</span>
        ) : (
          <div className="chatListe">
            {visibles.map((m, i) => (
              <div key={`${m.at}-${i}`}>
                <span className="sub">
                  <b style={{ color: "#FFFFFF" }}>{m.pseudo}</b> · {heure(m.at)}
                </span>
                <span>{m.text}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Questions(props: { d: BilanComplet }): JSX.Element {
  const b = props.d.bilan;
  const [table, setTable] = useState("");
  const visibles = b.questions.filter((q) => !table || q.tableId === table);
  const csv = (): void => {
    const lignes = [["Table", "Question", "Réponse", "Nombre"]];
    for (const q of b.questions) for (const r of q.reponses) lignes.push([q.table, q.question, r.reponse, String(r.n)]);
    const texte = lignes.map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(";")).join("\r\n");
    telecharger(`${nomFichier(b.nom)}-reponses.csv`, `﻿${texte}`, "text/csv;charset=utf-8");
  };
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <select className="chip" aria-label="Table" value={table} onChange={(e) => setTable(e.target.value)}>
          <option value="">Toutes les tables</option>
          {b.parTable.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nom}
            </option>
          ))}
        </select>
        <span className="sp" />
        <button type="button" className="btn s q" disabled={b.questions.length === 0} onClick={csv}>
          Télécharger les réponses (CSV)
        </button>
      </div>
      {visibles.length === 0 ? <div className="vide">Aucune question {table ? "à cette table" : "dans cet événement"}.</div> : null}
      <div className="grille2">
        {visibles.map((q) => {
          const max = Math.max(1, ...q.reponses.map((r) => r.n));
          return (
            <section className="carte" key={`${q.tableId}-${q.questionId}`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <b>{q.question}</b>
                <span className="sp" />
                <span className="pill g">{q.table}</span>
              </div>
              {q.reponses.map((r) => (
                <div className="jauge" key={r.reponse}>
                  <span>{r.reponse}</span>
                  <span>
                    <i style={{ width: `${(r.n / max) * 100}%` }} />
                  </span>
                  <span className="sub">{r.n}</span>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </>
  );
}

function TablesBilan(props: { d: BilanComplet }): JSX.Element {
  const b = props.d.bilan;
  return (
    <table>
      <thead>
        <tr>
          <th>Table</th>
          <th>Point de rencontre</th>
          <th>Rendez-vous</th>
          <th>Inscrits</th>
          <th>Arrivés</th>
          <th>Messages</th>
          <th>Issue</th>
        </tr>
      </thead>
      <tbody>
        {b.parTable.map((t) => (
          <tr key={t.id}>
            <td>
              <span className="nom">
                <Point couleur={couleurDe(t.couleurId)} />
                <span>
                  <b>{t.nom}</b>
                  <small>{t.sujet}</small>
                </span>
              </span>
            </td>
            <td>{t.point || "—"}</td>
            <td>{heure(t.rdvAt)}</td>
            <td>{t.inscrits}</td>
            <td>{t.arrives}</td>
            <td>{t.messages}</td>
            <td>{t.reunieA ? `réunie à ${heure(t.reunieA)}` : t.annulee ? "annulée" : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SignalementsBilan(props: { d: BilanComplet }): JSX.Element {
  const s = props.d.signalements;
  if (s.length === 0) return <div className="vide">Aucun signalement pendant l'événement.</div>;
  return (
    <table>
      <thead>
        <tr>
          <th>Reçu</th>
          <th>Motif</th>
          <th>Table</th>
          <th>Moment</th>
          <th>Décision</th>
        </tr>
      </thead>
      <tbody>
        {s.map((x) => (
          <tr key={x.id}>
            <td>{heure(x.recuA)}</td>
            <td>{MOTIFS[x.motif] ?? x.motif}</td>
            <td>
              <span className="nom">
                <Point couleur={x.tribu.couleur} />
                {x.tribu.nom}
              </span>
            </td>
            <td>{MOMENTS[x.phase] ?? x.phase}</td>
            <td>{x.decision ? `${DECISIONS[x.decision.type] ?? x.decision.type}${x.decision.motif ? ` — ${x.decision.motif}` : ""}` : "en attente"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------------ */
/* Le compte rendu PDF                                                  */
/* ------------------------------------------------------------------ */

const nomFichier = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "evenement";

function compteRendu(d: BilanComplet): void {
  const b = d.bilan;
  const pdf = new Pdf(`${b.nom} · compte rendu ${ESPACES_HUMAINS ? "Espaces Humains" : "Hoa"}`);
  pdf.titre(b.nom);
  pdf.texte(`${jourLong(b.debut)} · ${heure(b.debut)} – ${heure(b.fin)}${b.lieu ? ` · ${b.lieu}` : ""}`, { gris: true });
  if (b.gardeJusqua) pdf.texte(`Données gardées jusqu'au ${dateCourte(b.gardeJusqua)}.`, { gris: true, taille: 9 });
  pdf.espace(8);
  pdf.chiffres([
    [String(b.participants), pluriel(b.participants, "participant", "participants")],
    [String(b.tables), `${pluriel(b.tables, "table", "tables")}, ${b.tablesReunies} ${pluriel(b.tablesReunies, "réunie", "réunies")}`],
    [b.messages.toLocaleString("fr-FR"), pluriel(b.messages, "message", "messages")],
    [b.rencontresMoyennes.toLocaleString("fr-FR"), "personnes rencontrées en moyenne"],
    [String(d.signalements.length), pluriel(d.signalements.length, "signalement", "signalements")],
  ]);

  if (b.frequentation.length > 0) {
    pdf.section("Présents dans la zone");
    const max = Math.max(1, ...b.frequentation.map((f) => f.n));
    for (const f of b.frequentation) pdf.barre(heure(f.at), f.n, max);
  }

  pdf.section("Tables");
  pdf.tableau(
    ["Table", "Sujet", "Point de rencontre", "RDV", "Inscrits", "Arrivés", "Messages", "Issue"],
    b.parTable.map((t) => [t.nom, t.sujet, t.point || "—", heure(t.rdvAt), String(t.inscrits), String(t.arrives), String(t.messages), t.reunieA ? `réunie ${heure(t.reunieA)}` : t.annulee ? "annulée" : "—"]),
    [1.1, 1.6, 1.4, 0.6, 0.7, 0.7, 0.8, 1],
  );

  if (b.questions.length > 0) {
    pdf.section("Questions");
    for (const q of b.questions) {
      pdf.texte(`${q.question} — ${q.table}`, { gras: true, taille: 10.5, apres: 2 });
      const max = Math.max(1, ...q.reponses.map((r) => r.n));
      for (const r of q.reponses) pdf.barre(r.reponse, r.n, max);
      pdf.espace(4);
    }
  }

  if (d.signalements.length > 0) {
    pdf.section("Signalements");
    pdf.tableau(
      ["Reçu", "Motif", "Table", "Moment", "Décision"],
      d.signalements.map((x) => [heure(x.recuA), MOTIFS[x.motif] ?? x.motif, x.tribu.nom, MOMENTS[x.phase] ?? x.phase, x.decision ? (DECISIONS[x.decision.type] ?? x.decision.type) : "en attente"]),
      [0.6, 1.4, 1, 1.2, 1.2],
    );
  }

  pdf.section("Chats");
  for (const t of b.parTable) {
    const ms = d.messages.filter((m) => m.tableId === t.id);
    pdf.texte(`${t.nom}${t.sujet && t.sujet !== t.nom ? ` · ${t.sujet}` : ""} — ${ms.length} ${pluriel(ms.length, "message", "messages")}`, { gras: true, apres: 2 });
    if (ms.length === 0) pdf.texte("Aucun message.", { gris: true, taille: 9 });
    for (const m of ms) pdf.texte(`${heure(m.at)}  ${m.pseudo} : ${m.text}`, { taille: 9, apres: 1, retrait: 8 });
    pdf.espace(6);
  }

  telecharger(`${nomFichier(b.nom)}-compte-rendu.pdf`, pdf.octets(), "application/pdf");
}

